import { TemplatePrompts } from '@kimuno/api-sdk';
import type { Location } from 'found';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { recordTriggeredHint } from '../actions/user.actions';
import { LAUNCH_PARAM_KEYS } from '../components/AIAssistant/shared/templateLaunchParams';
import {
	podcastLaunchCampaignId,
	promoBannerHintKey,
} from '../components/PromoBanner/promoCampaigns';
import { routes } from '../constants';
import { selectTriggeredHints } from '../reducers/user.selectors';
import useAction from './useAction';

// Hints and banners that point the user at a destination, keyed by the exact
// `triggeredHints` key their visibility check reads. Reaching the destination
// concludes them for good, even if they were never displayed. Add an entry
// here for any new hint/banner that advertises a navigable target.
// Segment-boundary match: `/ki-tools` and `/ki-tools/<conversationId>` count,
// substring look-alikes (`/ki-toolsbar`, slugs containing "ki-tools") do not —
// a false positive would conclude a hint permanently.
const isOnAiToolsPage = ({ pathname }: Location) =>
	pathname === `/${routes.aiTools.root}` || pathname.startsWith(`/${routes.aiTools.root}/`);

export const targetRouteByHintKey: Record<string, (location: Location) => boolean> = {
	[promoBannerHintKey(podcastLaunchCampaignId)]: (location) =>
		isOnAiToolsPage(location) &&
		location.query?.[LAUNCH_PARAM_KEYS.template] === TemplatePrompts.CREATE_PODCAST,
};

export const useConcludeHintOnTargetRoute = (location: Location) => {
	const triggeredHints = useSelector(selectTriggeredHints);
	const recordTriggeredHintAction = useAction(recordTriggeredHint);

	// biome-ignore lint/correctness/useExhaustiveDependencies: recordTriggeredHintAction is recreated each render (useAction is unmemoized) but only wraps stable dispatch
	useEffect(() => {
		for (const [hintKey, isOnTargetRoute] of Object.entries(targetRouteByHintKey)) {
			if (!triggeredHints.includes(hintKey) && isOnTargetRoute(location)) {
				recordTriggeredHintAction(hintKey);
			}
		}
	}, [location, triggeredHints]);
};

export default useConcludeHintOnTargetRoute;
